/* =========================================================
   AURA EDITORIAL STORIES RC4
   - Hover de imagen secundaria en escritorio
   - Cambio automático de imagen en móvil
   - Hotspots por hover en escritorio
   - Hotspots por toque en móvil
   - Tarjeta de producto con miniatura
   - Quick Add
   - Lightbox
   - Navegación por teclado
   - Compatible con Shopify Theme Editor
========================================================= */

(() => {
  const SELECTOR = '[data-aura-stories]';
  const MOBILE_BREAKPOINT = 749;

  class AuraEditorialStories {
    constructor(root) {
      this.root = root;

      this.cards = Array.from(
        root.querySelectorAll('[data-story-card]')
      );

      this.hotspots = Array.from(
        root.querySelectorAll('[data-story-hotspot]')
      );

      this.lightbox = root.querySelector(
        '[data-aura-lightbox]'
      );

      this.lightboxStage = root.querySelector(
        '[data-aura-lightbox-stage]'
      );

      this.lightboxClose = root.querySelector(
        '[data-aura-lightbox-close]'
      );

      this.lightboxPrev = root.querySelector(
        '[data-aura-lightbox-prev]'
      );

      this.lightboxNext = root.querySelector(
        '[data-aura-lightbox-next]'
      );

      this.dataNode = root.querySelector(
        '[data-aura-stories-data]'
      );

      this.mobileQuery = window.matchMedia(
        `(max-width: ${MOBILE_BREAKPOINT}px)`
      );

      this.mobileInterval =
        Number(root.dataset.mobileInterval) || 3000;

      this.mobileTimers = new Map();
      this.stories = [];
      this.currentIndex = 0;
      this.observer = null;

      this.boundEvents = [];

      this.handleDocumentClick =
        this.handleDocumentClick.bind(this);

      this.handleKeydown =
        this.handleKeydown.bind(this);

      this.handleViewportChange =
        this.handleViewportChange.bind(this);

      this.parseStories();
      this.bindCards();
      this.bindHotspots();
      this.bindQuickAdd();
      this.bindLightbox();
      this.setupMobileObserver();
    }

    addEvent(element, eventName, handler, options) {
      if (!element) return;

      element.addEventListener(
        eventName,
        handler,
        options
      );

      this.boundEvents.push({
        element,
        eventName,
        handler,
        options
      });
    }

    parseStories() {
      if (!this.dataNode) return;

      try {
        this.stories = JSON.parse(
          this.dataNode.textContent.trim()
        );
      } catch (error) {
        console.error(
          '[AURA Editorial Stories] JSON inválido:',
          error
        );

        this.stories = [];
      }
    }

    /* =====================================================
       IMÁGENES PRINCIPAL Y SECUNDARIA
    ===================================================== */

    bindCards() {
      this.cards.forEach((card) => {
        const primary = card.querySelector(
          '.aura-story-card__image--primary'
        );

        const secondary = card.querySelector(
          '.aura-story-card__image--secondary'
        );

        if (!primary || !secondary) return;

        const enterHandler = () => {
          if (!this.mobileQuery.matches) {
            card.classList.add('is-secondary');
          }
        };

        const leaveHandler = (event) => {
          if (this.mobileQuery.matches) return;

          /*
           * No cambia la imagen mientras el usuario
           * esté interactuando con un hotspot.
           */
          if (
            event.relatedTarget?.closest?.(
              '[data-story-hotspot]'
            )
          ) {
            return;
          }

          card.classList.remove('is-secondary');
        };

        this.addEvent(
          card,
          'mouseenter',
          enterHandler
        );

        this.addEvent(
          card,
          'mouseleave',
          leaveHandler
        );
      });
    }

    setupMobileObserver() {
      if (!('IntersectionObserver' in window)) {
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const card = entry.target;

            if (
              this.mobileQuery.matches &&
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.45
            ) {
              this.startMobileSwap(card);
            } else {
              this.stopMobileSwap(card);
            }
          });
        },
        {
          threshold: [0, 0.45, 0.7]
        }
      );

      this.cards.forEach((card) => {
        this.observer.observe(card);
      });

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener(
          'change',
          this.handleViewportChange
        );
      } else if (this.mobileQuery.addListener) {
        this.mobileQuery.addListener(
          this.handleViewportChange
        );
      }
    }

    handleViewportChange() {
      this.cards.forEach((card) => {
        this.stopMobileSwap(card);
      });

      this.closeAllHotspots();
    }

    startMobileSwap(card) {
      const secondary = card.querySelector(
        '.aura-story-card__image--secondary'
      );

      if (!secondary) return;
      if (this.mobileTimers.has(card)) return;

      const timer = window.setInterval(() => {
        /*
         * No cambia la imagen si existe un hotspot abierto.
         */
        if (
          card.querySelector(
            '[data-story-hotspot].is-open'
          )
        ) {
          return;
        }

        card.classList.toggle('is-secondary');
      }, this.mobileInterval);

      this.mobileTimers.set(card, timer);
    }

    stopMobileSwap(card) {
      const timer = this.mobileTimers.get(card);

      if (timer) {
        window.clearInterval(timer);
      }

      this.mobileTimers.delete(card);
      card.classList.remove('is-secondary');
    }

    /* =====================================================
       HOTSPOTS
    ===================================================== */

    bindHotspots() {
      this.hotspots.forEach((hotspot) => {
        const openHotspot = () => {
          if (this.mobileQuery.matches) return;

          this.closeAllHotspots(hotspot);
          hotspot.classList.add('is-open');
          hotspot.setAttribute(
            'aria-expanded',
            'true'
          );
        };

        const closeHotspot = (event) => {
          if (this.mobileQuery.matches) return;

          const relatedTarget = event.relatedTarget;

          if (
            relatedTarget &&
            hotspot.contains(relatedTarget)
          ) {
            return;
          }

          hotspot.classList.remove('is-open');
          hotspot.setAttribute(
            'aria-expanded',
            'false'
          );
        };

        const clickHandler = (event) => {
          event.preventDefault();
          event.stopPropagation();

          const isOpen =
            hotspot.classList.contains('is-open');

          this.closeAllHotspots(hotspot);

          if (!isOpen) {
            hotspot.classList.add('is-open');
            hotspot.setAttribute(
              'aria-expanded',
              'true'
            );
          } else {
            hotspot.classList.remove('is-open');
            hotspot.setAttribute(
              'aria-expanded',
              'false'
            );
          }
        };

        const keyHandler = (event) => {
          if (
            event.key !== 'Enter' &&
            event.key !== ' '
          ) {
            return;
          }

          event.preventDefault();
          clickHandler(event);
        };

        this.addEvent(
          hotspot,
          'mouseenter',
          openHotspot
        );

        this.addEvent(
          hotspot,
          'mouseleave',
          closeHotspot
        );

        this.addEvent(
          hotspot,
          'click',
          clickHandler
        );

        this.addEvent(
          hotspot,
          'keydown',
          keyHandler
        );
      });

      document.addEventListener(
        'click',
        this.handleDocumentClick
      );
    }

    handleDocumentClick(event) {
      if (
        event.target.closest(
          '[data-story-hotspot]'
        )
      ) {
        return;
      }

      this.closeAllHotspots();
    }

    closeAllHotspots(except = null) {
      this.hotspots.forEach((hotspot) => {
        if (hotspot === except) return;

        hotspot.classList.remove('is-open');
        hotspot.setAttribute(
          'aria-expanded',
          'false'
        );
      });
    }

    /* =====================================================
       QUICK ADD
    ===================================================== */

    bindQuickAdd() {
      const forms = this.root.querySelectorAll(
        '[data-story-quick-add]'
      );

      forms.forEach((form) => {
        const submitHandler = async (event) => {
          event.preventDefault();
          event.stopPropagation();

          const button = form.querySelector(
            'button[type="submit"]'
          );

          if (!button || button.disabled) return;

          const originalText =
            button.textContent.trim();

          button.disabled = true;
          button.textContent = 'Agregando…';

          try {
            const endpoint =
              window.routes?.cart_add_url ||
              '/cart/add.js';

            const response = await fetch(endpoint, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'X-Requested-With':
                  'XMLHttpRequest'
              },
              body: new FormData(form)
            });

            if (!response.ok) {
              throw new Error(
                'No se pudo agregar el producto'
              );
            }

            await response.json();

            button.textContent = 'Agregado';

            document.dispatchEvent(
              new CustomEvent('cart:refresh', {
                bubbles: true
              })
            );

            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true
              })
            );

            window.setTimeout(() => {
              button.textContent = originalText;
              button.disabled = false;
            }, 1400);
          } catch (error) {
            console.error(
              '[AURA Editorial Stories]',
              error
            );

            button.textContent =
              'Intenta nuevamente';

            window.setTimeout(() => {
              button.textContent = originalText;
              button.disabled = false;
            }, 1700);
          }
        };

        this.addEvent(
          form,
          'submit',
          submitHandler
        );
      });
    }

    /* =====================================================
       LIGHTBOX
    ===================================================== */

    bindLightbox() {
      const openButtons = this.root.querySelectorAll(
        '[data-aura-story-open-button]'
      );

      openButtons.forEach((button) => {
        const openHandler = (event) => {
          /*
           * Evita abrir el lightbox cuando el clic
           * ocurre sobre un hotspot.
           */
          if (
            event.target.closest?.(
              '[data-story-hotspot]'
            )
          ) {
            return;
          }

          const card = button.closest(
            '[data-aura-story-open]'
          );

          if (!card) return;

          const index =
            Number(
              card.dataset.auraStoryOpen
            ) || 0;

          this.openLightbox(index);
        };

        this.addEvent(
          button,
          'click',
          openHandler
        );
      });

      this.addEvent(
        this.lightboxClose,
        'click',
        () => this.closeLightbox()
      );

      this.addEvent(
        this.lightboxPrev,
        'click',
        () => this.showPreviousStory()
      );

      this.addEvent(
        this.lightboxNext,
        'click',
        () => this.showNextStory()
      );

      this.addEvent(
        this.lightbox,
        'click',
        (event) => {
          if (event.target === this.lightbox) {
            this.closeLightbox();
          }
        }
      );

      document.addEventListener(
        'keydown',
        this.handleKeydown
      );
    }

    openLightbox(index) {
      if (
        !this.lightbox ||
        !this.stories.length
      ) {
        return;
      }

      this.closeAllHotspots();

      this.currentIndex = Math.max(
        0,
        Math.min(
          index,
          this.stories.length - 1
        )
      );

      this.renderLightboxStory();

      this.lightbox.classList.add('is-open');
      this.lightbox.setAttribute(
        'aria-hidden',
        'false'
      );

      document.documentElement.style.overflow =
        'hidden';

      this.lightboxClose?.focus();
    }

    closeLightbox() {
      if (!this.lightbox) return;

      this.lightbox.classList.remove('is-open');
      this.lightbox.setAttribute(
        'aria-hidden',
        'true'
      );

      document.documentElement.style.overflow = '';

      if (this.lightboxStage) {
        this.lightboxStage.innerHTML = '';
      }
    }

    showPreviousStory() {
      if (!this.stories.length) return;

      this.currentIndex =
        (
          this.currentIndex -
          1 +
          this.stories.length
        ) % this.stories.length;

      this.renderLightboxStory();
    }

    showNextStory() {
      if (!this.stories.length) return;

      this.currentIndex =
        (
          this.currentIndex + 1
        ) % this.stories.length;

      this.renderLightboxStory();
    }

    renderLightboxStory() {
      if (!this.lightboxStage) return;

      const story =
        this.stories[this.currentIndex];

      if (!story) return;

      this.lightboxStage.innerHTML = '';

      const wrapper =
        document.createElement('div');

      wrapper.className =
        'aura-stories-lightbox__media';

      if (
        story.type === 'video' &&
        story.video
      ) {
        const video =
          document.createElement('video');

        video.src = story.video;
        video.controls = true;
        video.autoplay = true;
        video.muted = false;
        video.loop = false;
        video.playsInline = true;

        wrapper.appendChild(video);
      } else if (story.image) {
        const image =
          document.createElement('img');

        image.src = story.image;
        image.alt = story.title || '';
        image.loading = 'eager';

        wrapper.appendChild(image);
      }

      if (story.title || story.caption) {
        const caption =
          document.createElement('div');

        caption.className =
          'aura-stories-lightbox__caption';

        if (story.title) {
          const title =
            document.createElement('strong');

          title.textContent = story.title;
          caption.appendChild(title);
        }

        if (story.caption) {
          const text =
            document.createElement('span');

          text.textContent = story.caption;
          caption.appendChild(text);
        }

        wrapper.appendChild(caption);
      }

      this.lightboxStage.appendChild(wrapper);
    }

    handleKeydown(event) {
      if (
        !this.lightbox?.classList.contains(
          'is-open'
        )
      ) {
        return;
      }

      if (event.key === 'Escape') {
        this.closeLightbox();
      }

      if (event.key === 'ArrowLeft') {
        this.showPreviousStory();
      }

      if (event.key === 'ArrowRight') {
        this.showNextStory();
      }
    }

    /* =====================================================
       DESTRUCCIÓN
    ===================================================== */

    destroy() {
      this.cards.forEach((card) => {
        this.stopMobileSwap(card);
      });

      this.observer?.disconnect();

      this.boundEvents.forEach(
        ({
          element,
          eventName,
          handler,
          options
        }) => {
          element.removeEventListener(
            eventName,
            handler,
            options
          );
        }
      );

      this.boundEvents = [];

      document.removeEventListener(
        'click',
        this.handleDocumentClick
      );

      document.removeEventListener(
        'keydown',
        this.handleKeydown
      );

      if (
        this.mobileQuery.removeEventListener
      ) {
        this.mobileQuery.removeEventListener(
          'change',
          this.handleViewportChange
        );
      } else if (
        this.mobileQuery.removeListener
      ) {
        this.mobileQuery.removeListener(
          this.handleViewportChange
        );
      }
    }
  }

  /* =======================================================
     INICIALIZACIÓN
  ======================================================= */

  const initializeSection = (root) => {
    if (!root) return;

    if (
      root.auraEditorialStoriesInstance
    ) {
      root.auraEditorialStoriesInstance.destroy();
    }

    root.auraEditorialStoriesInstance =
      new AuraEditorialStories(root);
  };

  const initializeAll = () => {
    document
      .querySelectorAll(SELECTOR)
      .forEach(initializeSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAll,
      { once: true }
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const root =
        event.target.matches?.(SELECTOR)
          ? event.target
          : event.target.querySelector?.(
              SELECTOR
            );

      initializeSection(root);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const root =
        event.target.matches?.(SELECTOR)
          ? event.target
          : event.target.querySelector?.(
              SELECTOR
            );

      root
        ?.auraEditorialStoriesInstance
        ?.destroy();
    }
  );
})();